import { Component, OnInit } from '@angular/core';
import { Challenge, ChallengeStatus, getChallengeStatusDisplay } from '../../../../../../models/challenge';
import { MyAccount } from '../../../../../../models/my-account';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '../../../../../../services/account/account.service';
import { ChallengeService } from '../../../../../../services/challenge/challenge.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { Association } from '../../../../../../models/association';
import { AssociationService } from '../../../../../../services/association/association.service';

@Component({
  selector: 'app-challenge-detail',
  templateUrl: './challenge-detail.component.html',
  styleUrls: ['./challenge-detail.component.css']
})
export class ChallengeDetailComponent implements OnInit {
  public isNewChallenge: boolean;
  public challenge: Challenge = {
    id: null,
    url: null,
    name: '',
    status: ChallengeStatus.draft,
    description: '',
    details: '',
    goal: null,
    auto_add_new_customers: false,
    auto_add_on_check_in: false,
    hide_in_climber_app: false,
    availability_start: null,
    availability_end: null,
    challenge_start: null,
    challenge_end: null,
    challenge_duration_days: null,
    will_prompt_for_prize: true,
    reward_instructions: '',
    challenge_completion_association_id: null,
    challenge_completion_association_name: null,
    mailchimp_added_to_challenge_event_name: '',
    mailchimp_challenge_completed_event_name: '',
    mailchimp_challenge_expired_event_name: '',
    created_at: null,
    updated_at: null,
  };
  public hasSetLengthOfTime = false;

  // If this is an existing challenge, store the existing status to determine
  // whether changing "Draft-edit-only" fields is allowed. We cannot simply
  // read the status of the challenge object because then any restricted
  // actions would take effect as soon as the status is changed locally, rather
  // than upon saving.
  public existingStatus: ChallengeStatus;

  public associations: Association[];

  public errorMessage = '';
  public errors: any = {};

  public loadingExistingChallenge = false;
  public loadingAssociations = true;
  public loadingSubmit = false;

  public challengeNotFound = false;
  public serverError: number = null;

  public myAccount: MyAccount;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public ChallengeStatus = ChallengeStatus;
  public getChallengeStatusDisplay = getChallengeStatusDisplay;

  constructor(
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private challengeService: ChallengeService,
    private associationService: AssociationService,
    private accountService: AccountService,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewChallenge = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    // Get the challenge to edit, if an ID was provided.
    if (!this.isNewChallenge) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.challengeNotFound = true;
        this.loadingExistingChallenge = false;
        return;
      }

      this.loadChallenge(idInt);
    }

    this.loadAssociations();
  }

  public loadChallenge(id: number) {
    this.loadingExistingChallenge = true;

    this.challengeService
      .getChallenge(id)
      .then(challenge => {
        this.challenge = challenge;
        this.existingStatus = challenge.status;
        if (challenge.challenge_duration_days) {
          this.hasSetLengthOfTime = true;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.challengeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingChallenge = false; });
  }

  public handleSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    if (this.hasSetLengthOfTime) {
      if (this.challenge.challenge_duration_days === null) {
        this.errors = {
          challenge_duration_days: 'This field is required.'
        };
        this.errorMessage = 'Please correct the errors shown and try again.';
        this.loadingSubmit = false;
        return;
      }

      this.challenge.challenge_start = null;
      this.challenge.challenge_end = null;
    } else {
      this.challenge.challenge_duration_days = null;
    }

    let savePromise: Promise<Challenge>;
    if (this.isNewChallenge) {
      savePromise = this.challengeService.addChallenge(this.challenge);
    } else {
      savePromise = this.challengeService.updateChallenge(this.challenge);
    }

    savePromise
      .then(savedChallenge => {
        this.challenge = savedChallenge;

        let successMessage: string;
        if (this.isNewChallenge) {
          successMessage = this.challenge.name + ' created successfully.';
        } else {
          successMessage = this.challenge.name + ' updated successfully.';
        }

        this.router.navigate(
          ['/challenges'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewChallenge) {
            this.errorMessage = 'You do not have permission to add challenges.';
          } else {
            this.errorMessage = 'You do not have permission to modify this challenge.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  /**
   * Get the list of available statuses for the challenge. This enforces the
   * rule that a challenge may not be reverted to Draft once it has become
   * Active or Retired.
   */
  public getAvailableStatuses(): ChallengeStatus[] {
    if (this.isExistingNonDraftChallenge()) {
      return [ChallengeStatus.active, ChallengeStatus.retired];
    } else {
      return [ChallengeStatus.draft, ChallengeStatus.active, ChallengeStatus.retired];
    }
  }

  /**
   * Whether the challenge is an existing, non-draft challenge.
   * This determines whether certain basic fields can be edited.
   */
  public isExistingNonDraftChallenge(): boolean {
    return !this.isNewChallenge && (this.existingStatus !== ChallengeStatus.draft);
  }

  private loadAssociations() {
    this.loadingAssociations = true;

    this.associationService
      .getAssociations(1, 3000, {})
      .then(result => {
        this.associations = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociations = false; });
  }

}
